import styled from '@emotion/styled';
import { Badge, Button, Icon, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useProductCatalogRepo } from '@hotjar/product-catalog';
import type { ProductInformation } from '@hotjar/product-catalog';
import { openPlanSelectionModal } from '@hotjar/service-management';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { noop } from '../../../../utils/function';
import type { BillingPeriod } from '~/subscription-management/api/common/model/subscription';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { ActionRequiredInfo } from './ActionRequiredInfo';
import type { DiscountState } from '~/subscription-management/utils/discounts';
import { Discount } from '~/subscription-management/components/discount/Discount';

export type EditableState = 'EDITABLE' | 'DISABLED' | 'HIDDEN';

export interface ProductSummaryItemProps {
  productInformation: ProductInformation;
  currentSubscriptionPlan: ProductInformation;
  futureProduct:
    | {
        product: ProductInformation;
        effectiveDate: string;
      }
    | undefined;
  editable: EditableState;
  isEdited?: boolean;
  billingPeriod: BillingPeriod;
  currency: Currency;
  onPlanChange(product: ProductInformation): void;
  isTwoSkusActionRequired: boolean;
  subscriptionEndDate?: string;
  legacyDiscount: DiscountState;
}

const S = {
  Grid: styled.div`
    display: grid;
    grid-template-columns: 75px 2fr auto;
    grid-column-gap: ${tokens.spacer.x3};
    justify-items: start;
    align-items: center;
    row-gap: ${tokens.spacer.x0};
  `,
  GridActionRequiredWide: styled.div`
    display: grid;
    grid-template-columns: 75px 1fr;
    grid-column-gap: ${tokens.spacer.x3};
    justify-items: start;
    align-items: center;
  `,
  Button: styled(Button)`
    justify-self: end;
    height: 30px;
    min-height: 30px;
  `,
  AllowancesText: styled(Text)`
    display: inline-block;
    margin-left: ${tokens.spacer.x0};
  `,
  Badge: styled(Badge)`
    margin-left: ${tokens.spacer.x4};
    text-transform: capitalize;
  `,
  Stack: styled(Stack)`
    align-items: center;
  `,
  Product: styled(Text)<{ alignTop: boolean }>`
    margin: 0;
    ${({ alignTop }) => alignTop && 'align-self: flex-start;'}
  `,
  CapitalizedText: styled(Text)`
    text-transform: capitalize;
  `,
  LegacyDiscount: styled(Discount)`
    grid-area: 2 / 2 / 2 / 2;
  `,
};

export function ProductSummaryItem({
  productInformation,
  currentSubscriptionPlan,
  futureProduct,
  billingPeriod,
  currency,
  editable,
  isEdited = false,
  onPlanChange,
  isTwoSkusActionRequired,
  subscriptionEndDate,
  legacyDiscount,
}: ProductSummaryItemProps): JSX.Element {
  const { t: tSubscriptions } = useTranslationWithPrefix('checkout:wizard.subscriptions');
  const { t: tProductInformation } = useTranslationWithPrefix('checkout:product-information');

  const product = useProductCatalogRepo({ billingPeriod, currency }).getPlansByProductId(
    productInformation.product.id,
  );

  const showFutureProductMessage = futureProduct !== undefined && !isEdited;

  // This is needed so the plan selection modal can select the correct plan if we are in the fair downgrade period
  // TODO: We need to make the UI less confusing, as mentioned in this discussion https://hotjar.slack.com/archives/C02FN088W6B/p1668010456408769
  const modalCurrentPlan = isEdited
    ? {
        id: productInformation.plan.id,
        name: productInformation.plan.name,
        planTier: productInformation.plan.planTier,
        allowance: productInformation.allowance,
      }
    : {
        id: futureProduct?.product.plan.id ?? productInformation.plan.id,
        name: futureProduct?.product.plan.name ?? productInformation.plan.name,
        planTier: futureProduct?.product.plan.planTier ?? productInformation.plan.planTier,
        allowance: futureProduct?.product.allowance ?? productInformation.allowance,
      };

  const showActionRequiredAlert =
    isTwoSkusActionRequired && productInformation.product.name === 'ask';
  const actionRequiredWideLayout = editable === 'HIDDEN';
  const GridComponent =
    showActionRequiredAlert && actionRequiredWideLayout ? S.GridActionRequiredWide : S.Grid;

  const openModal = async () => {
    if (!product) {
      return;
    }

    try {
      const plan = await openPlanSelectionModal({
        product,
        billingPeriod,
        currency,
        currentPlan: modalCurrentPlan,
        subscriptionEndDate,
        showActionRequiredAlert,
        currentSubscriptionPlan: {
          id: currentSubscriptionPlan.plan.id,
          name: currentSubscriptionPlan.plan.name,
          planTier: currentSubscriptionPlan.plan.planTier,
          allowance: currentSubscriptionPlan.allowance,
        },
      });
      onPlanChange(plan);
    } catch (error) {
      noop();
    }
  };

  return (
    <GridComponent>
      <S.Product alignTop={!!showFutureProductMessage} as="p">
        <Icon
          size="m"
          icon={productInformation.product.name === 'observe' ? 'hidden' : 'question'}
        />
        &nbsp;
        <Text textType="subtitle2">
          {tProductInformation(`product.${productInformation.product.name}`)}
        </Text>
      </S.Product>

      <S.Stack spacing="x0">
        <Stack direction="column" spacing="x0pt5">
          <div>
            {showActionRequiredAlert && (
              <ActionRequiredInfo
                subscriptionEndDate={subscriptionEndDate}
                actionRequiredWideLayout={actionRequiredWideLayout}
              />
            )}
            {!showActionRequiredAlert && (
              <>
                <S.CapitalizedText textType="subtitle2" textColor="text_light">
                  {tProductInformation(`plan.${productInformation.plan.name}`)}
                </S.CapitalizedText>
                &nbsp;
                <Text textType="body2" textColor="text_light">
                  {tSubscriptions('plan')}
                </Text>
                <S.AllowancesText textType="body2" textColor="text_light">
                  {'dailyCcSessions' in productInformation.allowance &&
                    tProductInformation('allowances.daily-sessions', {
                      allowances: productInformation.allowance.dailyCcSessions,
                    })}

                  {'monthlyResponses' in productInformation.allowance &&
                    tProductInformation('allowances.monthly-responses', {
                      allowances: productInformation.allowance.monthlyResponses,
                    })}
                </S.AllowancesText>
              </>
            )}
          </div>

          {showFutureProductMessage && (
            <Text textType="caption" textColor="text_light">
              {tSubscriptions('renewal-plan', {
                plan: capitalize(futureProduct.product.plan.name),
                date: futureProduct.effectiveDate,
              })}
            </Text>
          )}
        </Stack>

        {isEdited && (
          <S.Badge kind="custom" size="s" color={tokens.color.bermuda} textColor="white">
            {tSubscriptions('edited')}
          </S.Badge>
        )}
      </S.Stack>

      {editable !== 'HIDDEN' && (
        <S.Button onClick={openModal} kind="secondary" isDisabled={editable === 'DISABLED'}>
          {tSubscriptions('change-plan')}
        </S.Button>
      )}
      {legacyDiscount && productInformation.product.name === 'observe' && (
        <S.LegacyDiscount
          discount="legacy"
          state={legacyDiscount}
          useDefaultColor={legacyDiscount === 'applied'}
        />
      )}
    </GridComponent>
  );
}
